/**
 * Pure helpers for CustomMap's grouped sensor markers (N-245, "Grouped items
 * on map" design). Overlapping sensor icons collapse into one count badge;
 * clicking it opens a popup listing the grouped sensors.
 * Kept free of Leaflet/webpack so it runs under `node --test`.
 */

const {
  buildSelectedFieldMarkerIconHtml,
  buildUnselectedFieldMarkerIconHtml,
} = require('./mapMarkerIconHelpers');

// Desktop design: every icon sits in an invisible 48x48 px circle; icons
// whose circles overlap are grouped.
const SENSOR_GROUP_RADIUS_PX = 48;

// leaflet.markercluster options. Grouping stays on at every zoom (no
// disableClusteringAtZoom, no spiderfy at max zoom), so sensors on the same
// spot never stack; a badge click opens the list instead of zooming in.
const SENSOR_GROUP_CLUSTER_OPTIONS = {
  maxClusterRadius: SENSOR_GROUP_RADIUS_PX,
  showCoverageOnHover: false,
  spiderfyOnMaxZoom: false,
  zoomToBoundsOnClick: false,
};

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);

const buildSensorGroupBadgeHtml = (count) =>
  `<div class="sensor-group-badge">${escapeHtml(count)}</div>`;

const rowSortKey = (row) => `${row.fieldId}-${row.marker.transmitter_id}`;

const sortSensorGroupRows = (rows) =>
  [...rows].sort((a, b) => rowSortKey(a).localeCompare(rowSortKey(b)));

// Rows for a clicked cluster: CustomMap attaches each sensor's row to its
// L.marker as `sensorGroupRow`.
const buildSensorGroupRows = (childMarkers) =>
  sortSensorGroupRows(
    childMarkers.map((marker) => marker.sensorGroupRow).filter(Boolean),
  );

// Field id of the row a popup click landed on, or null for a click on the
// coordinates link (the link handles it) or outside any row.
const resolveSensorGroupRowClick = (target) => {
  if (!target || target.closest('a')) return null;
  const row = target.closest('.sensor-group-row');
  return row ? row.dataset.fieldId : null;
};

// Same icon the row's map pin would show.
const buildRowIconHtml = ({ selected, marker }) =>
  selected
    ? buildSelectedFieldMarkerIconHtml({
        info: marker.info,
        color: marker.color,
        type: marker.type,
      })
    : buildUnselectedFieldMarkerIconHtml();

// Same link and formatting as the single-sensor popup in CustomMap.
const buildCoordinatesLinkHtml = (coordinates, mapLinkUrl) => {
  const [lat, lng] = coordinates;
  const href = escapeHtml(`${mapLinkUrl}${coordinates}`);
  const latText = escapeHtml(parseFloat(lat).toFixed(6));
  const lngText = escapeHtml(parseFloat(lng).toFixed(6));
  return (
    `<a class="sensor-group-link" href="${href}" target="_blank" rel="noopener noreferrer">` +
    `<span>${latText}</span><span>${lngText}</span></a>`
  );
};

const buildRowLineHtml = (label, valueHtml) =>
  `<div class="sensor-group-row-line"><span class="sensor-group-row-label">${escapeHtml(
    label,
  )}</span>${valueHtml}</div>`;

const buildPlainValueHtml = (value) =>
  `<span class="sensor-group-row-value">${escapeHtml(value)}</span>`;

// Same name the single-sensor popup shows as its title; omitted when empty.
const buildNameLineHtml = (title, nameLabel) =>
  title ? buildRowLineHtml(nameLabel, buildPlainValueHtml(title)) : '';

const buildRowHtml = (row, labels) =>
  `<div class="sensor-group-row" data-field-id="${escapeHtml(
    row.fieldId,
  )}" data-sensor-id="${escapeHtml(row.sensorId)}">` +
  `<div class="sensor-group-row-icon">${buildRowIconHtml(row)}</div>` +
  '<div class="sensor-group-row-data">' +
  buildNameLineHtml(row.title, labels.nameLabel) +
  buildRowLineHtml(
    labels.transmitterIdLabel,
    buildPlainValueHtml(`#${row.marker.transmitter_id}`),
  ) +
  buildRowLineHtml(
    labels.coordinatesLabel,
    buildCoordinatesLinkHtml(row.marker.coordinates, labels.mapLinkUrl),
  ) +
  '</div></div>';

/**
 * @param rows   [{ fieldId, sensorId, selected, title, marker }] where marker
 *               is an all_farming_unit_markers entry and title is the
 *               single-sensor popup title (sensorsMap.get(type) || info)
 * @param labels { nameLabel, transmitterIdLabel, coordinatesLabel, mapLinkUrl }
 */
const buildSensorGroupListHtml = (rows, labels) =>
  '<div class="sensor-group-list">' +
  rows.map((row) => buildRowHtml(row, labels)).join('') +
  '</div>';

module.exports = {
  SENSOR_GROUP_RADIUS_PX,
  SENSOR_GROUP_CLUSTER_OPTIONS,
  buildSensorGroupBadgeHtml,
  buildSensorGroupListHtml,
  sortSensorGroupRows,
  buildSensorGroupRows,
  resolveSensorGroupRowClick,
};
